import { useMemo } from "react";

import { noop } from "lodash";

import { TabList, TabPanel, Tabs as UITabs } from "@similarweb/ui-components";

import useStickyObserver from "app/pages/sales-intelligence/hooks/useStickyObserver";
import type { NavigationTabConfig } from "app/pages/sales-intelligence/modules/company-analysis/types/navigation-tabs";

import { StyledTab, StyledTabsCarousel, StyledTabsAnchorRef, StyledTabsContainer, StyledTabsWrapper } from "./styles";
import "./Tabs.scss";
import { TabItem } from "./TabItem";
import useTabNavigation from "./useTabNavigation";

type Props = {
    config: readonly NavigationTabConfig[];
    onTabClick?: (tabName: string) => void;
    onSubTabClick?: (tabName: string) => void;
};

export const Tabs: React.FC<Props> = ({ config, onTabClick: onTabClickProp, onSubTabClick: onSubTabClickProp }) => {
    const filteredConfig = useMemo(() => config.filter((item) => !item.isHidden), [config]);

    const { tabIndex, subTabIndex, onTabClick, onSubTabClick } = useTabNavigation(
        filteredConfig,
        onTabClickProp,
        onSubTabClickProp,
    );

    const { ref: tabsAnchorRef, isSticky } = useStickyObserver();

    return (
        <>
            <StyledTabsAnchorRef ref={tabsAnchorRef} />
            <StyledTabsContainer
                data-automation="sticky-tabs"
                isSticky={isSticky}
                className="hidden-print"
            >
                <StyledTabsWrapper>
                    <UITabs
                        selectedIndex={tabIndex}
                        onSelect={noop}
                    >
                        <TabList>
                            <StyledTabsCarousel
                                offset={0}
                                margin={0}
                                wrapperBGColor={"transparent"}
                                showDividers={false}
                                isSticky={isSticky}
                            >
                                {filteredConfig.map((configItem, index) => (
                                    <StyledTab
                                        onClick={() => onTabClick(index)}
                                        key={configItem.stateId}
                                    >
                                        <TabItem
                                            config={configItem}
                                            isSelected={tabIndex === index}
                                            onSubTabClick={onSubTabClick}
                                            selectedSubTabIndex={subTabIndex}
                                        />
                                    </StyledTab>
                                ))}
                            </StyledTabsCarousel>
                        </TabList>
                        {filteredConfig.map((cfg) => (
                            // an “empty” panel to satisfy the 1:1 requirement
                            <TabPanel key={`${cfg.stateId}-panel`} />
                        ))}
                    </UITabs>
                </StyledTabsWrapper>
            </StyledTabsContainer>
        </>
    );
};
